import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Copy, ExternalLink, Loader2, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BadgePreviewPage() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('eventId');
  const style = searchParams.get('style') || 'standard';
  
  const [loading, setLoading] = useState(true);
  const [badgeData, setBadgeData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (eventId) {
      fetchBadge();
    }
  }, [eventId, style]);

  const fetchBadge = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/public/embed/badge/${eventId}?style=${style}`
      );
      setBadgeData(response.data);
    } catch (err) {
      setError('Failed to load badge');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Embed code copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (error || !badgeData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Award className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-700 mb-2">Badge Not Found</h2>
            <p className="text-gray-500">The requested badge could not be generated.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <Award className="w-12 h-12 text-purple-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Your Event Badge</h1>
          <p className="text-gray-600 mt-2">
            Add this badge to your website to show you're featured on Jigsaw Conferences
          </p>
        </div>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
            <div className="bg-white border rounded-lg p-8 flex justify-center">
              <div dangerouslySetInnerHTML={{ __html: badgeData.embedCode }} />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Embed Code</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(badgeData.embedCode)}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Code
              </Button>
            </div>
            <textarea
              readOnly
              value={badgeData.embedCode}
              className="w-full h-40 p-4 font-mono text-xs bg-gray-900 text-green-400 rounded-lg resize-none"
            />
            <p className="text-sm text-gray-500 mt-3">
              Paste this HTML code into your website where you want the badge to appear.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Details</h2>
            <div className="space-y-2 text-gray-600">
              <p><strong>Event:</strong> {badgeData.eventName}</p>
              <p><strong>Organiser:</strong> {badgeData.organiserName}</p>
              <p><strong>Year:</strong> {badgeData.year}</p>
            </div>
            <a
              href={badgeData.eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-orange-600 hover:text-orange-700 mt-4"
            >
              View event page
              <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Need help? Contact <a href="mailto:enquiries@jigsawconferences.co.uk" className="text-orange-600">enquiries@jigsawconferences.co.uk</a>
          </p>
        </div>
      </div>
    </div>
  );
}
